import React from 'react';
import { useTranslation } from 'react-i18next';
import { TimelineItemType } from 'types/TimelineTypes';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faComment, faCheck } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface TimelinePortalEventProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
}

const TimelinePortalEvent: React.FC<TimelinePortalEventProps> = ({ placement, item }) => {
    const { t } = useTranslation();
    const event = item.proposal_portal_event;

    if (!event) {
        return null;
    }

    const formattedDate = formatTimelineDate(item.ordering_date, false);

    // map each event type to an icon, label and css modifier
    const config = {
        opened: {
            icon: faEye as IconProp,
            label: t('timeline.portal.opened_label'),
            modifier: 'opened',
        },
        commented: {
            icon: faComment as IconProp,
            label: t('timeline.portal.commented_label'),
            modifier: 'commented',
        },
        accepted: {
            icon: faCheck as IconProp,
            label: t('timeline.portal.accepted_label'),
            modifier: 'accepted',
        },
    }[event.event_type];

    if (!config) {
        return null;
    }

    return (
        <div className={`timeline-item portal-event ${config.modifier}`}>
            {placement === 'page' && (
                <>
                    <div className='marker-container'>
                        <div className="big-marker">
                            <FontAwesomeIcon icon={config.icon} className='portal-event-icon fa-fw' />
                        </div>
                    </div>
                    <div className='update-content'>
                        <p className='update-text'>{config.label}</p>

                        {/* show accepted name and place when the proposal was accepted */}
                        {event.event_type === 'accepted' && event.accepted_name && (
                            <p className='update-text'>
                                {event.accepted_name}{event.accepted_place ? `, ${event.accepted_place}` : ''}
                            </p>
                        )}

                        {/* show the comment text when a comment was left */}
                        {event.event_type === 'commented' && event.comment && (
                            <blockquote className='portal-event-comment'>
                                {event.comment}
                            </blockquote>
                        )}

                        <p className='update-info'>{formattedDate}</p>
                    </div>
                </>
            )}

            {placement === 'summary' && (
                <div className='update-content'>
                    <div className='marker-container'>
                        <div className={`medium-marker portal-event ${config.modifier}`}>
                            <FontAwesomeIcon icon={config.icon} className='portal-event-icon fa-fw' />
                        </div>
                    </div>
                    <div>
                        <p className='update-text'>{config.label}</p>
                        <p className='update-info'>{formattedDate}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelinePortalEvent;
